import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileText, Clock, CheckCircle2, AlertCircle, ChevronRight,
  Loader2, ThumbsUp, ThumbsDown, Camera, X, PenLine, ShieldCheck, ReceiptText,
  Send, User,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { ROUTES } from '@/config/routes'
import AdminLayout from '@/components/admin/AdminLayout'
import useAuthStore from '@/stores/authStore'

const STATUS_LABEL = {
  pending:               { text: 'Pending',         color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25' },
  site_visit_required:   { text: 'Site Visit Req.', color: 'text-orange-400 bg-orange-500/10 border-orange-500/25' },
  site_visit_scheduled:  { text: 'Visit Scheduled', color: 'text-blue-400 bg-blue-500/10 border-blue-500/25'    },
  in_progress:           { text: 'In Progress',     color: 'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25' },
  uploaded:              { text: 'Uploaded',        color: 'text-green-400 bg-green-500/10 border-green-500/25'  },
}

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] ?? { text: status, color: 'text-ink-muted bg-surface-overlay border-surface-border' }
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full border font-medium ${s.color}`}>
      {s.text}
    </span>
  )
}

function StatCard({ icon: Icon, label, value, color, onClick, highlight }) {
  return (
    <div
      onClick={onClick}
      className={`bg-surface-raised border rounded-xl p-4 flex items-center gap-3 ${onClick ? 'cursor-pointer hover:border-siglat-teal/50 transition-colors' : ''} ${highlight && (value ?? 0) > 0 ? 'border-yellow-500/40' : 'border-surface-border'}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xl font-bold text-ink-primary leading-none">{value ?? '—'}</p>
        <p className="text-xs text-ink-muted mt-0.5">{label}</p>
      </div>
      {onClick && <ChevronRight className="w-3.5 h-3.5 text-ink-muted shrink-0" />}
    </div>
  )
}

// ── Pending Completion Card ────────────────────────────────────────────────────

function CompletionCard({ booking, onResolved }) {
  const profile = useAuthStore((s) => s.profile)
  const [expanded,   setExpanded]   = useState(false)
  const [rejecting,  setRejecting]  = useState(false)
  const [note,       setNote]       = useState('')
  const [acting,     setActing]     = useState(false)
  const [photoUrls,  setPhotoUrls]  = useState([])
  const [photosLoaded, setPhotosLoaded] = useState(false)

  async function loadPhotos() {
    if (photosLoaded) return
    const { data } = await supabase
      .from('booking_photos')
      .select('photo_path')
      .eq('booking_id', booking.id)
      .eq('photo_type', 'after')
      .order('created_at', { ascending: false })
      .limit(6)

    const urls = await Promise.all(
      (data ?? []).map(async (row) => {
        const { data: signed } = await supabase.storage
          .from('job-photos')
          .createSignedUrl(row.photo_path, 3600)
        return signed?.signedUrl ?? null
      })
    )
    setPhotoUrls(urls.filter(Boolean))
    setPhotosLoaded(true)
  }

  function handleExpand() {
    setExpanded((v) => {
      if (!v) loadPhotos()
      return !v
    })
  }

  async function handleApprove() {
    setActing(true)
    const { error } = await supabase
      .from('bookings')
      .update({ status: 'completed', completed_at: new Date().toISOString() })
      .eq('id', booking.id)
    if (!error) {
      supabase.functions.invoke('send-notification', { body: {
        type: 'job_completed', customer_id: booking.customer_id, worker_id: booking.worker_id,
        data: { ref_number: booking.ref_number },
      }}).catch(console.error)
      onResolved(booking.id, 'approved')
    }
    setActing(false)
  }

  async function handleReject() {
    if (!note.trim()) return
    setActing(true)
    // Reset to in_progress so worker must re-submit
    const { error } = await supabase
      .from('bookings')
      .update({ status: 'in_progress' })
      .eq('id', booking.id)
    // Log note via notification (best-effort)
    if (!error) {
      await supabase.from('notifications').insert({
        user_id:  booking.worker_id,
        type:     'completion_rejected',
        title:    'Job completion rejected',
        message:  note.trim(),
        booking_id: booking.id,
      })
      supabase.functions.invoke('send-notification', { body: {
        type: 'job_rejected_worker', worker_id: booking.worker_id,
        data: { ref_number: booking.ref_number, note: note.trim() },
      }}).catch(console.error)
      onResolved(booking.id, 'rejected')
    }
    setActing(false)
  }

  return (
    <div className="bg-surface-raised border border-pink-500/30 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={handleExpand}
        className="w-full px-4 py-3.5 text-left hover:bg-surface-overlay transition-colors flex items-center justify-between gap-3"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-xs px-2 py-0.5 rounded-full border font-medium text-pink-400 bg-pink-500/10 border-pink-500/25">
              Pending Approval
            </span>
            <p className="text-sm font-semibold text-ink-primary">
              {booking.ref_number ?? booking.id.slice(0, 8).toUpperCase()}
            </p>
          </div>
          <p className="text-xs text-ink-muted">
            Worker: {booking._workerName ?? '—'} · Client: {booking._customerName ?? '—'}
          </p>
          <p className="text-xs text-ink-muted truncate mt-0.5">{booking.address ?? '—'}</p>
        </div>
        <Camera className="w-4 h-4 text-ink-muted shrink-0" />
      </button>

      {/* Expanded */}
      {expanded && (
        <div className="border-t border-surface-border px-4 py-4 flex flex-col gap-4">

          {/* After photos */}
          {photoUrls.length > 0 ? (
            <div>
              <p className="text-[10px] text-ink-muted uppercase tracking-widest mb-2">After Photos</p>
              <div className="grid grid-cols-3 gap-1.5">
                {photoUrls.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={url}
                      alt={`After photo ${i + 1}`}
                      className="w-full aspect-square object-cover rounded-lg border border-surface-border hover:opacity-90 transition-opacity"
                    />
                  </a>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-ink-muted italic">No after photos uploaded.</p>
          )}

          {/* Approve */}
          {!rejecting && (
            <button
              onClick={handleApprove}
              disabled={acting}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-green-500/10 border border-green-500/25 text-green-400 text-sm font-medium hover:bg-green-500/20 transition-colors disabled:opacity-50"
            >
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
              Approve Completion
            </button>
          )}

          {/* Reject flow */}
          {!rejecting ? (
            <button
              onClick={() => setRejecting(true)}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-siglat-red/10 border border-siglat-red/25 text-siglat-red text-sm font-medium hover:bg-siglat-red/20 transition-colors"
            >
              <ThumbsDown className="w-4 h-4" />
              Reject — Send Back
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-ink-muted uppercase tracking-widest">Rejection Reason (sent to worker)</p>
                <button onClick={() => setRejecting(false)}>
                  <X className="w-3.5 h-3.5 text-ink-muted" />
                </button>
              </div>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. After photos are unclear, please re-submit with better lighting"
                className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-siglat-red resize-none"
              />
              <button
                onClick={handleReject}
                disabled={acting || !note.trim()}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-siglat-red/10 border border-siglat-red/25 text-siglat-red text-sm font-medium hover:bg-siglat-red/20 transition-colors disabled:opacity-40"
              >
                {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4" />}
                Confirm Rejection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Dispatch Card ─────────────────────────────────────────────────────────────

function DispatchCard({ booking, workers, onDispatched }) {
  const [selectedWorker, setSelectedWorker] = useState('')
  const [dispatching,    setDispatching]    = useState(false)
  const [done,           setDone]           = useState(false)

  async function handleDispatch() {
    if (!selectedWorker) return
    setDispatching(true)
    const { error } = await supabase
      .from('bookings')
      .update({ status: 'assigned', worker_id: selectedWorker })
      .eq('id', booking.id)
    if (!error) {
      supabase.functions.invoke('send-notification', { body: {
        type: 'worker_assigned', customer_id: booking.customer_id, worker_id: selectedWorker,
        data: { ref_number: booking.ref_number, address: booking.address },
      }}).catch(console.error)
      supabase.functions.invoke('send-notification', { body: {
        type: 'job_assigned_worker', worker_id: selectedWorker,
        data: { ref_number: booking.ref_number, address: booking.address },
      }}).catch(console.error)
      setDone(true)
      setTimeout(() => onDispatched(booking.id), 800)
    }
    setDispatching(false)
  }

  return (
    <div className={`bg-surface-raised border rounded-xl px-4 py-3.5 flex flex-col gap-3 transition-colors ${done ? 'border-green-500/30' : 'border-blue-500/25'}`}>
      <div>
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <p className="text-sm font-semibold text-ink-primary">
            {booking._customerName ?? '—'}
          </p>
          {done && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/25 text-green-400 font-medium">
              Dispatched ✓
            </span>
          )}
        </div>
        <p className="text-xs font-mono text-blue-400">{booking.ref_number ?? '—'}</p>
        {booking.address && (
          <p className="text-xs text-ink-muted mt-0.5 truncate">{booking.address}</p>
        )}
        {booking.service_categories?.name && (
          <p className="text-xs text-ink-muted">{booking.service_categories.name}</p>
        )}
      </div>

      {!done && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-muted pointer-events-none" />
            <select
              value={selectedWorker}
              onChange={(e) => setSelectedWorker(e.target.value)}
              className="w-full appearance-none bg-surface-overlay border border-surface-border rounded-lg pl-8 pr-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-siglat-teal"
            >
              <option value="">Select worker…</option>
              {workers.map((w) => (
                <option key={w.id} value={w.id}>{w.full_name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleDispatch}
            disabled={dispatching || !selectedWorker}
            className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/25 text-blue-400 text-xs font-semibold hover:bg-blue-500/20 transition-colors disabled:opacity-40"
          >
            {dispatching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Dispatch
          </button>
        </div>
      )}
    </div>
  )
}

// ── Contract Sign Card ────────────────────────────────────────────────────────

function ContractSignCard({ contract, onSigned }) {
  const adminProfile = useAuthStore((s) => s.profile)
  const [signing, setSigning] = useState(false)
  const [done,    setDone]    = useState(false)

  async function handleSign() {
    setSigning(true)
    const now = new Date().toISOString()
    const { error } = await supabase
      .from('contracts')
      .update({
        status:       'fully_signed',
        sk_signed_by: adminProfile?.id ?? null,
        sk_signed_at: now,
      })
      .eq('id', contract.id)

    if (!error) {
      supabase.functions.invoke('generate-contract-pdf', {
        body: { contract_id: contract.id },
      }).catch(console.error)
      supabase.functions.invoke('send-notification', { body: {
        type: 'contract_signed', customer_id: contract.customer_id,
        data: { ref_number: contract.quote_reference },
      }}).catch(console.error)
      setDone(true)
      setTimeout(() => onSigned(contract.id), 800)
    }
    setSigning(false)
  }

  return (
    <div className={`bg-surface-raised border rounded-xl px-4 py-3.5 flex items-center justify-between gap-3 transition-colors ${done ? 'border-green-500/30' : 'border-siglat-teal/30'}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <p className="text-sm font-semibold text-ink-primary">
            {contract._customerName ?? '—'}
          </p>
          {done && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/25 text-green-400 font-medium">
              Signed ✓
            </span>
          )}
        </div>
        <p className="text-xs font-mono text-siglat-teal">{contract.quote_reference ?? '—'}</p>
        <p className="text-xs text-ink-muted mt-0.5">
          Signed by client on {contract.customer_signed_at
            ? new Date(contract.customer_signed_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
            : '—'}
        </p>
      </div>
      {!done ? (
        <button
          onClick={handleSign}
          disabled={signing}
          className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-siglat-teal/10 border border-siglat-teal/30 text-siglat-teal text-xs font-semibold hover:bg-siglat-teal/20 transition-colors disabled:opacity-50"
        >
          {signing
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <PenLine className="w-3.5 h-3.5" />}
          Countersign
        </button>
      ) : (
        <ShieldCheck className="w-5 h-5 text-green-400 shrink-0" />
      )}
    </div>
  )
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const navigate = useNavigate()

  const [stats,             setStats]            = useState(null)
  const [recent,            setRecent]           = useState([])
  const [pending,           setPending]          = useState([])  // bookings in pending_approval
  const [pendingContracts,  setPendingContracts]  = useState([]) // contracts awaiting SK signature
  const [confirmedBookings, setConfirmedBookings] = useState([]) // ready to dispatch
  const [workers,           setWorkers]           = useState([]) // worker profiles
  const [loading,           setLoading]           = useState(true)

  useEffect(() => {
    async function fetchData() {
      // Stats — parallel queries
      const [pendingProposalRes, activeRes, totalRes, pendingApprovalRes, pendingContractRes, pendingPaymentRes] = await Promise.all([
        supabase
          .from('final_proposal_requests')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('bookings')
          .select('id', { count: 'exact', head: true })
          .in('status', ['confirmed', 'assigned', 'in_progress']),
        supabase
          .from('final_proposal_requests')
          .select('id', { count: 'exact', head: true }),
        supabase
          .from('bookings')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending_approval'),
        supabase
          .from('contracts')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending_sk'),
        supabase
          .from('payment_receipts')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
      ])

      setStats({
        pending:          pendingProposalRes.count  ?? 0,
        active:           activeRes.count           ?? 0,
        total:            totalRes.count            ?? 0,
        pendingApproval:  pendingApprovalRes.count  ?? 0,
        pendingContracts: pendingContractRes.count  ?? 0,
        pendingPayments:  pendingPaymentRes.count   ?? 0,
      })

      // Recent 5 proposal requests
      const { data: rows } = await supabase
        .from('final_proposal_requests')
        .select(`
          id, status, created_at, property_type,
          quotes ( quote_reference, booking_id,
            bookings ( address, service_categories ( name ) )
          ),
          profiles ( full_name )
        `)
        .order('created_at', { ascending: false })
        .limit(5)

      setRecent(rows ?? [])

      // Bookings awaiting completion approval
      const { data: approvalBks } = await supabase
        .from('bookings')
        .select('id, ref_number, address, customer_id, worker_id, completed_at')
        .eq('status', 'pending_approval')
        .order('completed_at', { ascending: true })
        .limit(20)

      if (approvalBks?.length) {
        const custIds   = [...new Set(approvalBks.map((b) => b.customer_id).filter(Boolean))]
        const workerIds = [...new Set(approvalBks.map((b) => b.worker_id).filter(Boolean))]
        const allIds    = [...new Set([...custIds, ...workerIds])]

        const { data: profilesData } = await supabase
          .from('profiles').select('id, full_name').in('id', allIds)

        const profMap = {}
        for (const p of profilesData ?? []) profMap[p.id] = p.full_name

        setPending(approvalBks.map((b) => ({
          ...b,
          _customerName: profMap[b.customer_id] ?? '—',
          _workerName:   profMap[b.worker_id]   ?? '—',
        })))
      } else {
        setPending([])
      }

      // Contracts awaiting SK countersignature
      const { data: contractRows } = await supabase
        .from('contracts')
        .select('id, customer_id, quote_reference, customer_signed_at, status')
        .eq('status', 'pending_sk')
        .order('customer_signed_at', { ascending: true })
        .limit(20)

      if (contractRows?.length) {
        const custIds = [...new Set(contractRows.map((c) => c.customer_id).filter(Boolean))]
        const { data: contractProfiles } = await supabase
          .from('profiles').select('id, full_name').in('id', custIds)
        const cpMap = {}
        for (const p of contractProfiles ?? []) cpMap[p.id] = p.full_name
        setPendingContracts(contractRows.map((c) => ({
          ...c,
          _customerName: cpMap[c.customer_id] ?? '—',
        })))
      } else {
        setPendingContracts([])
      }

      // Confirmed bookings — ready to dispatch (no worker assigned yet)
      const [confirmedRes, workersRes] = await Promise.all([
        supabase
          .from('bookings')
          .select('id, ref_number, address, customer_id, service_categories ( name )')
          .eq('status', 'confirmed')
          .is('worker_id', null)
          .order('created_at', { ascending: true })
          .limit(20),
        supabase
          .from('profiles')
          .select('id, full_name')
          .eq('role', 'worker')
          .order('full_name'),
      ])

      setWorkers(workersRes.data ?? [])

      if (confirmedRes.data?.length) {
        const custIds = [...new Set(confirmedRes.data.map((b) => b.customer_id).filter(Boolean))]
        const { data: custProfiles } = await supabase
          .from('profiles').select('id, full_name').in('id', custIds)
        const cpMap = {}
        for (const p of custProfiles ?? []) cpMap[p.id] = p.full_name
        setConfirmedBookings(confirmedRes.data.map((b) => ({
          ...b,
          _customerName: cpMap[b.customer_id] ?? '—',
        })))
      } else {
        setConfirmedBookings([])
      }

      setLoading(false)
    }

    fetchData()
  }, [])

  function handleResolved(bookingId) {
    setPending((prev) => prev.filter((b) => b.id !== bookingId))
    setStats((prev) => prev
      ? { ...prev, pendingApproval: Math.max(0, (prev.pendingApproval ?? 1) - 1) }
      : prev
    )
  }

  function handleDispatched(bookingId) {
    setConfirmedBookings((prev) => prev.filter((b) => b.id !== bookingId))
    setStats((prev) => prev ? { ...prev, active: (prev.active ?? 0) + 1 } : prev)
  }

  function handleContractSigned(contractId) {
    setPendingContracts((prev) => prev.filter((c) => c.id !== contractId))
    setStats((prev) => prev
      ? { ...prev, pendingContracts: Math.max(0, (prev.pendingContracts ?? 1) - 1) }
      : prev
    )
  }

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold text-ink-primary">Dashboard</h1>
          <p className="text-sm text-ink-muted mt-0.5">Siglat Konstract — Admin Panel</p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
          <StatCard
            icon={AlertCircle}
            label="Pending Proposals"
            value={stats?.pending}
            color="bg-yellow-500/15 text-yellow-400"
          />
          <StatCard
            icon={Clock}
            label="Active Bookings"
            value={stats?.active}
            color="bg-siglat-teal/15 text-siglat-teal"
          />
          <StatCard
            icon={FileText}
            label="Total Requests"
            value={stats?.total}
            color="bg-surface-overlay text-ink-muted"
          />
          <StatCard
            icon={CheckCircle2}
            label="Awaiting Approval"
            value={stats?.pendingApproval}
            color="bg-pink-500/15 text-pink-400"
          />
          <StatCard
            icon={PenLine}
            label="Contracts to Sign"
            value={stats?.pendingContracts}
            color="bg-siglat-teal/15 text-siglat-teal"
          />
          <StatCard
            icon={ReceiptText}
            label="Pending Payments"
            value={stats?.pendingPayments}
            color="bg-yellow-500/15 text-yellow-400"
            onClick={() => navigate(ROUTES.ADMIN_FINANCIALS)}
            highlight
          />
        </div>

        {/* ── Contracts awaiting SK countersignature ─────────────────────────── */}
        {(loading || pendingContracts.length > 0) && (
          <div className="mb-8">
            <p className="text-sm font-semibold text-ink-primary mb-3 flex items-center gap-2">
              Contracts Awaiting Your Signature
              {pendingContracts.length > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-siglat-teal/15 text-siglat-teal border border-siglat-teal/25 font-medium">
                  {pendingContracts.length}
                </span>
              )}
            </p>

            {loading ? (
              <div className="h-20 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
            ) : (
              <div className="flex flex-col gap-2">
                {pendingContracts.map((ct) => (
                  <ContractSignCard
                    key={ct.id}
                    contract={ct}
                    onSigned={handleContractSigned}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Confirmed bookings — dispatch queue ────────────────────────────── */}
        {(loading || confirmedBookings.length > 0) && (
          <div className="mb-8">
            <p className="text-sm font-semibold text-ink-primary mb-3 flex items-center gap-2">
              Ready to Dispatch
              {confirmedBookings.length > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/25 font-medium">
                  {confirmedBookings.length}
                </span>
              )}
            </p>
            {loading ? (
              <div className="h-20 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
            ) : (
              <div className="flex flex-col gap-2">
                {confirmedBookings.map((bk) => (
                  <DispatchCard
                    key={bk.id}
                    booking={bk}
                    workers={workers}
                    onDispatched={handleDispatched}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Pending job completion approvals ───────────────────────────────── */}
        {(loading || pending.length > 0) && (
          <div className="mb-8">
            <p className="text-sm font-semibold text-ink-primary mb-3 flex items-center gap-2">
              Pending Job Completion Approvals
              {pending.length > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-400 border border-pink-500/25 font-medium">
                  {pending.length}
                </span>
              )}
            </p>

            {loading ? (
              <div className="h-20 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
            ) : (
              <div className="flex flex-col gap-2">
                {pending.map((bk) => (
                  <CompletionCard
                    key={bk.id}
                    booking={bk}
                    onResolved={handleResolved}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Recent proposals ───────────────────────────────────────────────── */}
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-semibold text-ink-primary">Recent Proposal Requests</p>
          <button
            onClick={() => navigate(ROUTES.ADMIN_QUOTES)}
            className="text-xs text-siglat-teal hover:underline flex items-center gap-0.5"
          >
            View all <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-20 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
            ))}
          </div>
        ) : recent.length === 0 ? (
          <div className="bg-surface-raised border border-surface-border rounded-xl p-8 text-center">
            <CheckCircle2 className="w-8 h-8 text-ink-muted mx-auto mb-2" />
            <p className="text-sm text-ink-muted">No proposal requests yet.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {recent.map((row) => {
              const quote   = row.quotes
              const booking = quote?.bookings
              const catName = booking?.service_categories?.name ?? 'Service'
              const address = booking?.address ?? ''
              const city    = address.split(',').pop()?.trim() ?? address
              const dateStr = new Date(row.created_at).toLocaleDateString('en-PH', {
                month: 'short', day: 'numeric', year: 'numeric',
              })
              return (
                <button
                  key={row.id}
                  onClick={() => navigate(ROUTES.ADMIN_QUOTES)}
                  className="w-full bg-surface-raised border border-surface-border rounded-xl px-4 py-3 text-left hover:border-siglat-teal/40 transition-colors flex items-center justify-between gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-sm font-medium text-ink-primary truncate">
                        {row.profiles?.full_name ?? 'Unknown'}
                      </p>
                      <StatusBadge status={row.status} />
                    </div>
                    <p className="text-xs text-siglat-teal font-mono">
                      {quote?.quote_reference ?? '—'}
                    </p>
                    <p className="text-xs text-ink-muted mt-0.5">
                      {catName}{city ? ' \u2014 ' + city : ''} &bull; {dateStr}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-ink-muted shrink-0" />
                </button>
              )
            })}
          </div>
        )}

      </div>
    </AdminLayout>
  )
}
